import { BusinessStreams } from 'ciam-commons';

/* istanbul ignore file */
export interface CambridgeProduct {
  title: string;
  description: string;
  imageUrl: string;
  url: string;
}

export interface SourceSystem {
  orgId: string;
  systemId: string;
}
export interface CambridgeOrganisation {
  bpid: string;
  name: string;
  sourceSystems?: SourceSystem[];
}

export interface Identities {
  provider: string;
  email: string;
}

export interface User {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  country: string;
  hasPassword: boolean;
  organisationId: string;
  organisations: CambridgeOrganisation[];
  profilePicture?: string;
  email?: string;
  verifiedEmails?: string[];
  unverifiedEmails?: string[];
  products: CambridgeProduct[];
  lastLogin?: string;
  socialProviders: string[];
  organisationName: string;
  organisationLegacyNumber: string;
  isCambridgeAssessor: boolean;
  subscriptions: ISubscriptions;
  roles: string[];
  identities?: Identities[];
}

export type SubscriptionsSchema = {
  fields: SubscriptionsSchemaFields;
};

export type SubscriptionsSchemaFields = {
  [key: string]: SubscriptionsSchemaField | SubscriptionsSchemaFieldWithName;
};

export type SubscriptionsSchemaField = {
  email: {
    type: 'subscription';
    required: boolean;
    doubleOptIn: boolean;
    description: string;
  };
};

export type SubscriptionsSchemaFieldWithName = {
  email: {
    type: 'subscription';
    required: boolean;
    doubleOptIn: boolean;
    description: string;
  };
  name: string;
};
export interface Notification {
  id?: string;
  title?: string;
  text?: string;
  variant?: 'success' | 'warning' | 'error';
  onClick?(): void;
  removing?: boolean;
  timeout?: number;
}

export interface AccountInfo {
  UID: string;
  hasPassword?: boolean;
  lastLoginTimestamp?: number;
  socialProviders?: string;
  data?: AccountInfoData;
  profile: ProfileData;
  loginIDs: LoginIDsData;
  groups: Organizations;
  subscriptions: ISubscriptions;
  identities?: Identities[];
}

export interface AccountInfoData {
  claims: ClaimsData;
  lastSelectedOrg?: string;
}

export interface ProfileData {
  firstName?: string;
  lastName?: string;
  country?: string;
  email?: string;
  photoURL?: string;
  thumbnailURL?: string;
}

export interface LoginIDsData {
  emails?: string[];
  unverifiedEmails?: string[];
}
export interface ClaimsData {
  roles: RoleData[];
  orgs: OrgData[];
  businessStream?: string;
  platformAccess?: Platform[];
}

export interface RoleData {
  bpid: string;
  roleNames: string[];
}

export type OrgData = CambridgeOrganisation;

export interface Platform {
  platformURL?: string;
  bpid?: string;
  description?: string;
  iconURL?: string;
  platformName?: string;
}

export interface Organizations {
  organizations: Organization[];
}

export interface Organization {
  bpid: string;
  orgId: string;
  orgName: string;
  roles: string[];
  status?: string;
}

export interface EnvVariables {
  NODE_ENV: string;
  API_KEY: string;
  CLIENT_ID: string;
  PROXY_BASE_URL: string;
  FEEDBACK_SURVEY_URL: string;
  FEATURE_FLAG_PROXY_URL: string;
  FEATURE_FLAG_CLIENT_KEY: string;
  FEATURE_FLAG_APP_NAME: string;
  FEATURE_FLAG_REFRESH_INTERVAL: number;
  ORG_USERS_BASE_URL: string;
  ICP_LICENSE_STRING: string;
  IS_CHINA: boolean;
  GA_TRACKING_ID: string;
  CAS_BPID: string;
  CA_BPID: string;
  CDN_DATA_CENTER: string;
  HOTJAR_SITE_ID: string;
}

export const enum AuthStatus {
  Error = 'Error',
  Authenticated = 'Authenticated',
  Unauthenticated = 'Unauthenticated',
  Loading = 'Loading',
}

export enum AppPage {
  Home = 'Home',
  Account = 'Account',
  PersonalInfo = 'Personal info',
  Communications = 'Communications',
  ConnectedAccounts = 'Connected accounts',
  AddUser = 'Add User',
  ContactUs = 'Contact Us',
  MyOrganisation = 'My organisation',
}

export interface ISubscription {
  email: ISubscriptionEmail;
}

export interface ISubscriptions {
  [key: string]: ISubscription;
}

export interface ISubscriptionEmail {
  isSubscribed: boolean;
  tags?: string[];
  lastUpdatedSubscriptionState?: string;
  doubleOptIn?: IDoubleOptIn;
}

export interface IDoubleOptIn {
  emailSentTime?: string;
  confirmTime?: string | null;
  status?: OptInStatus;
}

export enum OptInStatus {
  Confirmed = 'Confirmed',
  Pending = 'Pending',
  NotConfirmed = 'NotConfirmed',
}

export const enum OrgUserStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Registered = 'REGISTERED',
  Invited = 'INVITED',
}

export interface NewOrgUser
  extends Omit<
    OrgUser,
    'UID' | 'profilePicture' | 'status' | 'roles' | 'examinerId' | 'systemIDs'
  > {
  roles: string[];
}

export type OrgUser = {
  UID: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture?: string;
  status: OrgUserStatus;
  roles?: string[];
  creditorNumber?: string;
  businessStream?: BusinessStreams[];
  examinerId: string;
  systemIDs: SystemIDs[];
  invitedDate?: number;
};

export interface OrgUserDetails extends OrgUser {
  lastLoginTimestamp?: number;
}

export type OrgRoles = {
  roles: RoleInfo[];
};

export type RoleInfo = {
  name: string;
  alias: string;
  description: string;
  isShared: boolean;
  internal: boolean;
};

export const enum PreferenceRadioOption {
  Yes = 'Yes',
  No = 'No',
}

export type ValidationType = 'error' | 'warning' | 'success';

export const enum Section {
  country = 'Country',
  name = 'Name',
  password = 'Password',
  photo = 'Photo',
  email = 'Email',
}

module.exports = {
  AppPage,
};

export const enum GACategory {
  personalInfo = 'Personal Info',
  products = 'Products',
  navigation = 'Navigation',
  userMenu = 'User Menu',
  externalLinks = 'External Links',
  internalLinks = 'Internal Links',
}

export interface EnvConfig {
  CA_BPID: string;
  CAS_BPID: string;
}

export interface TooltipContent {
  [key: string]: React.ReactNode;
}

export interface SystemIDs {
  idType: string;
  idValue: string;
}

export type OrgAppRoles = {
  appRoles: AppRoleInfo[];
};

export type AppRoleInfo = {
  platform: string;
  roles: RoleInfo[];
  expand: boolean;
};
